import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin,
  Clock,
  CalendarDays,
  Home,
  Share2,
  Shirt,
  Waves,
  SprayCan,
  Bed,
  BookOpen,
  Pill,
  Backpack,
  AlertTriangle,
  Cross,
  ChevronRight,
  HeartPulse,
  UtensilsCrossed,
  Bus,
  Mic2,
  Gamepad2,
  Baby,
  Ticket,
  Siren,
  Phone,
  MessageCircle,
  Sunrise,
  Sun,
  Moon,
} from "lucide-react";

const CAMP_ADDRESS = "3201 County Rd 225, Marengo, OH 43334";
const MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CAMP_ADDRESS)}`;
const MAPS_EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(CAMP_ADDRESS)}&output=embed`;

// Normaliza número a formato tel/whatsapp
const digitsOnly = (p) => (p || "").replace(/\D/g, "");
const withCountry = (p) => {
  const d = digitsOnly(p);
  return d.length === 10 ? `1${d}` : d;
};

const AREAS = [
  {
    key: "botiquin",
    icon: HeartPulse,
    title: "Botiquín / Farmacia",
    lead: "Luz María",
    phone: "614-680-1490",
    color: "from-red-500/30 to-red-500/5",
    ring: "ring-red-400/50",
    accent: "text-red-300",
    desc: "Medicamentos, primeros auxilios y curaciones. Si te sientes mal, tienes fiebre, dolor o una lesión, busca a Luz María de inmediato. Trae tus medicamentos personales rotulados con tu nombre.",
    bullets: [
      "Marca todos tus medicamentos con nombre y dosis",
      "Avisa cualquier alergia al llegar",
      "Disponible las 24 horas durante el campamento",
    ],
  },
  {
    key: "cocina",
    icon: UtensilsCrossed,
    title: "Comidas / Cocina",
    lead: null,
    phone: null,
    color: "from-orange-500/30 to-orange-500/5",
    ring: "ring-orange-400/50",
    accent: "text-orange-300",
    desc: "Desayuno, almuerzo y cena servidos en el comedor. Se cubren todas las comidas del viernes noche al domingo mediodía.",
    bullets: [
      "Avisa de alergias o dietas especiales al inscribirte",
      "Respeta los horarios de comida",
      "Snacks disponibles en ciertos momentos",
    ],
  },
  {
    key: "transporte",
    icon: Bus,
    title: "Transporte",
    lead: null,
    phone: null,
    color: "from-blue-500/30 to-blue-500/5",
    ring: "ring-blue-400/50",
    accent: "text-blue-300",
    desc: "Salida el Viernes 12 PM en punto desde la iglesia. Llega 30 min antes para acomodar equipaje. Regreso el Domingo después del servicio de cierre.",
    bullets: [
      "Punto de encuentro: Primera Iglesia del Nazareno",
      "Hora de salida: Viernes 12:00 PM (llegar 11:30 AM)",
      "Regreso: Domingo tarde después del culto",
    ],
  },
  {
    key: "servicios",
    icon: Mic2,
    title: "Servicios / Alabanza",
    lead: null,
    phone: null,
    color: "from-purple-500/30 to-purple-500/5",
    ring: "ring-purple-400/50",
    accent: "text-purple-300",
    desc: "Servicio de apertura el viernes, noche de adoración el sábado y culto de cierre el domingo. Ministración con el Pastor Cristian Rosario.",
    bullets: [
      "Trae tu Biblia y libreta",
      "Participa con corazón dispuesto",
      "Vístete apropiado para el culto del domingo",
    ],
  },
  {
    key: "piscina",
    icon: Waves,
    title: "Piscina / Acuáticas",
    lead: null,
    phone: null,
    color: "from-cyan-500/30 to-cyan-500/5",
    ring: "ring-cyan-400/50",
    accent: "text-cyan-300",
    desc: "Actividades acuáticas supervisadas. Se debe cumplir el código de vestimenta cristiano descrito en la lista de qué llevar.",
    bullets: [
      "Chicas: traje de una sola pieza + short encima",
      "Varones: short de baño apropiado + camiseta",
      "Salir del área siempre con salida de baño o camiseta",
    ],
  },
  {
    key: "juegos",
    icon: Gamepad2,
    title: "Juegos / Deportes",
    lead: null,
    phone: null,
    color: "from-green-500/30 to-green-500/5",
    ring: "ring-green-400/50",
    accent: "text-green-300",
    desc: "Competencias por equipos, dinámicas grupales, canchas deportivas y juegos temáticos durante todo el fin de semana.",
    bullets: [
      "Trae ropa deportiva cómoda",
      "Tenis cerrados obligatorios para deportes",
      "Diviértete con respeto y buen espíritu",
    ],
  },
  {
    key: "cabanas",
    icon: Bed,
    title: "Cabañas / Hospedaje",
    lead: null,
    phone: null,
    color: "from-amber-500/30 to-amber-500/5",
    ring: "ring-amber-400/50",
    accent: "text-amber-300",
    desc: "Cabañas separadas por género con camas tamaño Twin. Debes traer tus propias sábanas, almohadas y cobija.",
    bullets: [
      "Sábanas tamaño Twin obligatorias",
      "Almohada y cobija/manta personal",
      "Respeta el horario de silencio nocturno",
    ],
  },
  {
    key: "ninos",
    icon: Baby,
    title: "Niños (3-11 años)",
    lead: null,
    phone: null,
    color: "from-pink-500/30 to-pink-500/5",
    ring: "ring-pink-400/50",
    accent: "text-pink-300",
    desc: "Programa especial para menores con actividades adaptadas por edad y supervisión constante de líderes capacitados.",
    bullets: [
      "3-6 años: $55 (con acompañante)",
      "7-11 años: $150",
      "Los padres reciben reportes de sus hijos durante el fin de semana",
    ],
  },
  {
    key: "inscripciones",
    icon: Ticket,
    title: "Registro / Inscripciones",
    lead: null,
    phone: null,
    color: "from-yellow-500/30 to-yellow-500/5",
    ring: "ring-yellow-400/50",
    accent: "text-yellow-300",
    desc: "Todos deben estar 100% pagados antes del 28 de agosto. Consulta tu progreso de pagos en 'Mi Cuenta'.",
    bullets: [
      "12+ años: $250 · 7-11 años: $150 · 3-6 años: $55",
      "Plan de pago: diario, semanal, quincenal o mensual",
      "Métodos: Square, Zelle, Cash App, efectivo",
    ],
  },
  {
    key: "emergencia",
    icon: Siren,
    title: "Emergencia general",
    lead: null,
    phone: null,
    color: "from-rose-500/30 to-rose-500/5",
    ring: "ring-rose-400/50",
    accent: "text-rose-300",
    desc: "Contacta al líder principal ante cualquier situación urgente durante el campamento. En caso de emergencia médica llama al 911.",
    bullets: [
      "911 para emergencias reales",
      "Iglesia: 614-508-0303",
      "Luz María (Botiquín): 614-680-1490",
    ],
  },
];

const ITINERARY = [
  {
    day: "Viernes",
    icon: Sunrise,
    color: "from-orange-500/20 to-transparent",
    accent: "text-orange-300",
    subtitle: "Día de llegada",
    blocks: [
      { time: "11:30 AM", title: "Reunión en la iglesia", desc: "Llega puntual con tu equipaje. Registro y acomodación en vehículos." },
      { time: "12:00 PM", title: "Salida hacia el campamento", desc: "Viaje grupal en oración. ¡Prepárate mentalmente para el encuentro!" },
      { time: "Tarde", title: "Llegada y check-in", desc: "Asignación de cabañas, acomodación de equipaje y recorrido por las instalaciones." },
      { time: "Noche", title: "Cena de bienvenida", desc: "Primer momento juntos como familia." },
      { time: "Noche", title: "Servicio de apertura", desc: "Ministración con el Pastor Cristian Rosario. ¡Comienza el rescate!" },
      { time: "Silencio", title: "Descanso", desc: "Horario de silencio nocturno." },
    ],
  },
  {
    day: "Sábado",
    icon: Sun,
    color: "from-yellow-500/20 to-transparent",
    accent: "text-yellow-300",
    subtitle: "Día de encuentro",
    blocks: [
      { time: "Mañana", title: "Devocional matutino", desc: "Momento personal con Dios antes del desayuno." },
      { time: "Mañana", title: "Desayuno", desc: "Comida caliente para empezar el día con energía." },
      { time: "Mañana", title: "Servicio de la mañana", desc: "Enseñanza y alabanza." },
      { time: "Tarde", title: "Almuerzo", desc: "Comida en el comedor." },
      { time: "Tarde", title: "Actividades: piscina, juegos y deportes", desc: "Diversión sana con supervisión. Compite, ríe, hazte amigos." },
      { time: "Tarde", title: "Cena", desc: "Recarga para la noche fuerte." },
      { time: "Noche", title: "Noche de adoración", desc: "El momento más poderoso del campamento. Ministración profunda." },
      { time: "Silencio", title: "Descanso", desc: "Horario de silencio nocturno." },
    ],
  },
  {
    day: "Domingo",
    icon: Moon,
    color: "from-purple-500/20 to-transparent",
    accent: "text-purple-300",
    subtitle: "Día de restauración",
    blocks: [
      { time: "Mañana", title: "Devocional final", desc: "Últimas palabras del Señor antes de regresar." },
      { time: "Mañana", title: "Desayuno + empacar", desc: "Recoge tus cosas y deja tu cabaña limpia." },
      { time: "Mediodía", title: "Regreso a la iglesia", desc: "Viaje de vuelta." },
      { time: "Tarde", title: "Servicio de cierre en la iglesia", desc: "Culto especial con vestimenta apropiada. Testimonios de lo que Dios hizo." },
    ],
  },
];

const RULES = [
  "Respeta a tus líderes, compañeros y las instalaciones del campamento.",
  "Participa activamente en los servicios, devocionales y actividades programadas.",
  "Mantén un lenguaje y comportamiento apropiado para un ambiente cristiano.",
  "No salir del área del campamento sin permiso de un líder responsable.",
  "Los varones y las chicas deben permanecer en sus áreas asignadas para dormir.",
  "Uso del celular con moderación: prioriza estar presente en cada momento del encuentro.",
  "Cuida el descanso: respeta los horarios de silencio nocturno.",
  "Ven con actitud de servicio y disposición a dejarte transformar por Dios.",
];

const PACKING = [
  {
    icon: Shirt,
    title: "Ropa",
    items: [
      "Ropa cómoda para viernes, sábado y domingo",
      "Ropa cómoda/deportiva para juegos y actividades",
      "Pijama",
      "Ropa interior y medias",
      "Tenis o zapatos cómodos",
      "Chancletas",
      "Suéter o chaqueta ligera",
      "Ropa apropiada para el servicio de cierre del domingo",
      "Una muda adicional por cualquier imprevisto",
    ],
  },
  {
    icon: Waves,
    title: "Traje de baño — Chicas",
    items: [
      "Traje de baño de una sola pieza (entero)",
      "Evitar bikinis o trajes reveladores",
      "Short de baño para usar encima",
      "Salida de baño, camiseta o ropa cómoda para cubrirse",
      "Sandalias",
      "Toalla",
    ],
  },
  {
    icon: Waves,
    title: "Traje de baño — Varones",
    items: [
      "Franelas o polo shirts",
      "Short o pantalón de baño apropiado",
      "Evitar shorts excesivamente cortos o ajustados",
      "No utilizar ropa interior como traje de baño",
      "Camiseta para salir de la piscina",
      "Sandalias y toalla",
    ],
  },
  {
    icon: SprayCan,
    title: "Higiene personal",
    items: [
      "Cepillo y pasta dental",
      "Jabón de baño",
      "Shampoo y acondicionador",
      "Desodorante",
      "Toalla de baño y toallita para la cara",
      "Peine o cepillo para el cabello",
      "Productos personales / higiene femenina",
      "Toallitas húmedas",
      "Bolsa para ropa sucia",
    ],
  },
  {
    icon: Bed,
    title: "Para dormir",
    items: ["Sábanas (cama Twin)", "Almohadas", "Cobija o manta", "Artículos personales para dormir"],
  },
  {
    icon: BookOpen,
    title: "Tiempo espiritual",
    items: ["Biblia", "Libreta o cuaderno", "Bolígrafo o lápiz"],
  },
  {
    icon: Pill,
    title: "Artículos personales",
    items: [
      "Medicamentos personales",
      "Lentes o lentes de contacto",
      "Repelente de insectos",
      "Protector solar",
      "Otros artículos indispensables",
    ],
  },
  {
    icon: Backpack,
    title: "Otros",
    items: ["Celular", "Cargador", "Power bank", "Linterna pequeña", "Mochila pequeña para actividades"],
  },
];

// ----------------- COMPONENT -----------------
export default function CampInfo() {
  const nav = useNavigate();
  const [openArea, setOpenArea] = useState(null); // area object or "itinerary"

  const share = async () => {
    const shareData = {
      title: "Operación Rescate · Info del campamento",
      text: "Toda la info del campamento Operación Rescate: itinerario, encargados, qué llevar, reglas y ubicación.",
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Enlace copiado");
      }
    } catch { /* cancelled */ }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="camp-info-page">
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(250,204,21,0.12),_transparent_60%)] pointer-events-none" />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-6">
        <Button
          variant="ghost"
          onClick={() => nav("/")}
          data-testid="info-home-button"
          className="text-white/80 hover:text-yellow-400 hover:bg-white/5"
        >
          <Home className="w-4 h-4 mr-2" /> Inicio
        </Button>
        <Button
          onClick={share}
          data-testid="info-share-button"
          className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest text-xs px-4"
        >
          <Share2 className="w-4 h-4 mr-2" /> Compartir
        </Button>
      </div>

      {/* Hero */}
      <section className="relative z-10 px-5 sm:px-10 pt-4 pb-10 max-w-5xl mx-auto text-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-block px-4 py-2 border border-yellow-400/40 bg-yellow-400/10 text-yellow-400 text-[10px] sm:text-xs uppercase tracking-[0.4em] font-bold"
        >
          Info del Campamento
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-6 font-heading uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight"
          data-testid="info-title"
        >
          Operación
          <br />
          <span className="text-gradient-gold">Rescate</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mt-6 text-white/70 text-sm sm:text-base uppercase tracking-[0.3em]"
        >
          Todo lo que necesitas saber, en un solo lugar
        </motion.p>
      </section>

      {/* Key info cards */}
      <section className="relative z-10 px-5 sm:px-10 pb-10 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border-2 border-yellow-400/40 bg-gradient-to-br from-yellow-400/10 to-transparent p-6 backdrop-blur-sm" data-testid="info-card-date">
            <CalendarDays className="w-6 h-6 text-yellow-400 mb-3" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">Fecha</div>
            <div className="font-heading text-2xl sm:text-3xl text-white tracking-wide">28 · Agosto · 2026</div>
            <div className="mt-2 text-white/60 text-sm">Viernes a domingo</div>
          </div>
          <div className="border-2 border-yellow-400/40 bg-gradient-to-br from-yellow-400/10 to-transparent p-6 backdrop-blur-sm" data-testid="info-card-departure">
            <Clock className="w-6 h-6 text-yellow-400 mb-3" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">Salida</div>
            <div className="font-heading text-2xl sm:text-3xl text-white tracking-wide">Viernes · 12 PM</div>
            <div className="mt-2 text-white/60 text-sm">Desde la iglesia. ¡Llega puntual!</div>
          </div>
          <div className="border-2 border-yellow-400/40 bg-gradient-to-br from-yellow-400/10 to-transparent p-6 backdrop-blur-sm" data-testid="info-card-address">
            <MapPin className="w-6 h-6 text-yellow-400 mb-3" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">Dirección</div>
            <div className="text-white text-base sm:text-lg leading-snug font-semibold">
              3201 County Rd 225<br />Marengo, OH 43334
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="info-maps-link"
              className="mt-3 inline-flex items-center gap-1 text-yellow-400 hover:text-yellow-300 text-xs uppercase tracking-widest font-bold"
            >
              Abrir en Google Maps <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Hub grid */}
      <section className="relative z-10 px-5 sm:px-10 pb-16 max-w-6xl mx-auto" data-testid="info-hub-section">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-400/70 mb-2">Toca cualquier ícono</div>
          <h2 className="font-heading uppercase text-4xl sm:text-5xl text-white tracking-tight">
            Hub del <span className="text-gradient-gold">Campamento</span>
          </h2>
          <p className="mt-3 text-white/60 text-sm max-w-xl mx-auto">
            Todo lo que necesitas saber sobre cada área: responsables, contactos y detalles importantes.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-5">
          {/* Itinerary card (special, first) */}
          <HubCard
            onClick={() => setOpenArea("itinerary")}
            icon={CalendarDays}
            title="Itinerario"
            subtitle="Viernes a domingo"
            color="from-yellow-400/40 to-yellow-500/10"
            ring="ring-yellow-400"
            accent="text-yellow-300"
            featured
            testid="hub-card-itinerary"
          />
          {AREAS.map((a) => (
            <HubCard
              key={a.key}
              onClick={() => setOpenArea(a)}
              icon={a.icon}
              title={a.title}
              subtitle={a.lead || "Toca para ver"}
              color={a.color}
              ring={a.ring}
              accent={a.accent}
              testid={`hub-card-${a.key}`}
            />
          ))}
        </div>
      </section>

      {/* Map */}
      <section className="relative z-10 px-5 sm:px-10 pb-16 max-w-5xl mx-auto" data-testid="info-map-section">
        <div className="border-2 border-yellow-400/40 bg-black/40 backdrop-blur-sm overflow-hidden shadow-2xl shadow-yellow-500/10">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-yellow-400/20 bg-yellow-400/5">
            <div className="flex items-center gap-3 min-w-0">
              <MapPin className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-400/70">Ubicación</div>
                <div className="text-white text-sm font-semibold truncate">{CAMP_ADDRESS}</div>
              </div>
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1 text-yellow-400 hover:text-yellow-300 text-xs uppercase tracking-widest font-bold whitespace-nowrap"
            >
              Cómo llegar <ChevronRight className="w-3 h-3" />
            </a>
          </div>
          <iframe
            title="Mapa del campamento"
            src={MAPS_EMBED_URL}
            width="100%"
            height="340"
            style={{ border: 0, display: "block", filter: "grayscale(0.15) contrast(1.05)" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="info-map-iframe"
          />
        </div>
      </section>

      {/* Rules */}
      <section className="relative z-10 px-5 sm:px-10 pb-16 max-w-5xl mx-auto" data-testid="info-rules-section">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-yellow-400/10 border border-yellow-400/40 flex items-center justify-center flex-shrink-0">
            <Cross className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-400/70">Convivencia</div>
            <h2 className="font-heading uppercase text-3xl sm:text-4xl text-white tracking-tight">Reglas del campamento</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {RULES.map((r, i) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-white/5 border-l-2 border-yellow-400/60">
              <span className="font-heading text-yellow-400 text-xl leading-none w-6 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-white/85 text-sm leading-relaxed">{r}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Packing */}
      <section className="relative z-10 px-5 sm:px-10 pb-16 max-w-5xl mx-auto" data-testid="info-packing-section">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-yellow-400/10 border border-yellow-400/40 flex items-center justify-center flex-shrink-0">
            <Backpack className="w-5 h-5 text-yellow-400" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-400/70">Prepárate</div>
            <h2 className="font-heading uppercase text-3xl sm:text-4xl text-white tracking-tight">Qué llevar</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {PACKING.map((sec, idx) => {
            const Icon = sec.icon;
            return (
              <div key={sec.title} className="border border-white/10 bg-white/[0.03] p-6" data-testid={`packing-section-${idx}`}>
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-yellow-400/20">
                  <Icon className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  <h3 className="font-heading uppercase text-yellow-400 text-lg tracking-wider">{sec.title}</h3>
                </div>
                <ul className="space-y-2">
                  {sec.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80 text-sm leading-relaxed">
                      <span className="text-yellow-400/60 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Important */}
      <section className="relative z-10 px-5 sm:px-10 pb-16 max-w-5xl mx-auto">
        <div className="border-2 border-yellow-400 bg-yellow-400/10 backdrop-blur-sm p-6 sm:p-8" data-testid="info-important-section">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
            <h3 className="font-heading uppercase text-yellow-400 text-2xl tracking-wider">Importante</h3>
          </div>
          <p className="text-white/90 text-sm sm:text-base leading-relaxed">
            <span className="font-bold text-yellow-400">Marca tus pertenencias personales</span>, especialmente aquellas que puedan confundirse con las de otros. Lleva solamente lo necesario y evita traer objetos de mucho valor.
          </p>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative z-10 border-t border-white/10 px-5 sm:px-10 py-12 text-center">
        <div className="font-heading uppercase text-3xl sm:text-5xl text-gradient-gold tracking-tight mb-3">
          Nos vemos allá
        </div>
        <p className="text-white/50 text-xs uppercase tracking-[0.3em] mb-6">
          Un encuentro que puede cambiar tu vida
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => nav("/")} data-testid="info-footer-home" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest">
            <Home className="w-4 h-4 mr-2" /> Volver al inicio
          </Button>
          <Button onClick={share} variant="outline" data-testid="info-footer-share" className="border-yellow-400/60 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold uppercase tracking-widest bg-transparent">
            <Share2 className="w-4 h-4 mr-2" /> Compartir con otros
          </Button>
        </div>
      </section>

      {/* --- Modal --- */}
      <Dialog open={!!openArea} onOpenChange={(v) => !v && setOpenArea(null)}>
        <DialogContent
          className="max-w-2xl bg-black border border-yellow-400/40 text-white p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
          data-testid="hub-modal"
        >
          {openArea === "itinerary" ? (
            <ItineraryContent />
          ) : openArea ? (
            <AreaContent area={openArea} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------- SUB COMPONENTS -----------------
function HubCard({ onClick, icon: Icon, title, subtitle, color, ring, accent, featured, testid }) {
  return (
    <motion.button
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      data-testid={testid}
      className={`group relative flex flex-col items-center text-center p-4 sm:p-5 border ${featured ? "border-yellow-400" : "border-white/10"} bg-gradient-to-br ${color} hover:border-yellow-400/60 transition-all rounded-xl`}
    >
      <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center ring-2 ${ring} mb-3 group-hover:scale-110 transition-transform`}>
        <Icon className={`w-7 h-7 sm:w-9 sm:h-9 ${accent}`} />
      </div>
      <div className="font-heading uppercase text-white text-sm sm:text-base tracking-wider leading-tight">
        {title}
      </div>
      <div className="text-white/50 text-[11px] mt-1 line-clamp-1">
        {subtitle}
      </div>
      {featured && (
        <div className="absolute -top-2 -right-2 bg-yellow-400 text-black text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
          Ver
        </div>
      )}
    </motion.button>
  );
}

function AreaContent({ area }) {
  const Icon = area.icon;
  const tel = area.phone ? `tel:+${withCountry(area.phone)}` : null;
  const wa = area.phone ? `https://wa.me/${withCountry(area.phone)}` : null;
  return (
    <div>
      <div className={`px-6 pt-6 pb-4 bg-gradient-to-br ${area.color} border-b border-yellow-400/20`}>
        <div className="flex items-start gap-4">
          <div className={`w-16 h-16 rounded-full bg-black/60 flex items-center justify-center ring-2 ${area.ring} flex-shrink-0`}>
            <Icon className={`w-8 h-8 ${area.accent}`} />
          </div>
          <div className="flex-1 min-w-0">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-white text-2xl sm:text-3xl tracking-wide text-left">
                {area.title}
              </DialogTitle>
            </DialogHeader>
            {area.lead && (
              <div className="mt-2 text-white/80 text-sm">
                Encargado: <span className="font-semibold text-white">{area.lead}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        <p className="text-white/85 text-sm sm:text-base leading-relaxed">{area.desc}</p>

        {area.bullets && (
          <ul className="space-y-2">
            {area.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                <span className="text-yellow-400 mt-1">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {area.phone ? (
          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-white/10">
            <a href={tel} className="flex-1" data-testid="modal-call-button">
              <Button className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest">
                <Phone className="w-4 h-4 mr-2" /> Llamar {area.phone}
              </Button>
            </a>
            <a href={wa} target="_blank" rel="noreferrer" className="flex-1" data-testid="modal-whatsapp-button">
              <Button variant="outline" className="w-full border-green-500 text-green-400 hover:bg-green-500 hover:text-black font-bold uppercase tracking-widest bg-transparent">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
            </a>
          </div>
        ) : (
          <div className="pt-2 border-t border-white/10 text-white/50 text-xs uppercase tracking-widest text-center italic">
            Encargado por confirmar
          </div>
        )}
      </div>
    </div>
  );
}

function ItineraryContent() {
  return (
    <div>
      <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-yellow-400/20 to-transparent border-b border-yellow-400/20">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-black/60 flex items-center justify-center ring-2 ring-yellow-400 flex-shrink-0">
            <CalendarDays className="w-8 h-8 text-yellow-300" />
          </div>
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-white text-2xl sm:text-3xl tracking-wide text-left">
              Itinerario del fin de semana
            </DialogTitle>
            <p className="text-white/60 text-xs uppercase tracking-widest mt-1">Viernes a domingo · sujetos a cambios</p>
          </DialogHeader>
        </div>
      </div>

      <div className="px-6 py-5 space-y-8">
        {ITINERARY.map((day) => {
          const DIcon = day.icon;
          return (
            <div key={day.day} data-testid={`itinerary-day-${day.day.toLowerCase()}`}>
              <div className={`flex items-center gap-3 mb-4 pb-3 border-b border-yellow-400/20 bg-gradient-to-r ${day.color} -mx-6 px-6 py-3`}>
                <DIcon className={`w-6 h-6 ${day.accent}`} />
                <div>
                  <div className={`font-heading uppercase text-2xl tracking-wide ${day.accent}`}>{day.day}</div>
                  <div className="text-white/50 text-[10px] uppercase tracking-widest">{day.subtitle}</div>
                </div>
              </div>
              <ol className="space-y-3">
                {day.blocks.map((b, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-20 text-right">
                      <span className="text-yellow-400 text-xs font-bold uppercase tracking-widest">{b.time}</span>
                    </div>
                    <div className="flex-1 pb-3 border-l-2 border-yellow-400/30 pl-4 -ml-2">
                      <div className="text-white font-semibold text-sm">{b.title}</div>
                      <div className="text-white/60 text-xs mt-0.5 leading-relaxed">{b.desc}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          );
        })}

        <div className="border border-yellow-400/40 bg-yellow-400/5 p-4 text-center">
          <p className="text-white/70 text-xs uppercase tracking-widest">
            Los horarios exactos se anuncian al llegar al campamento
          </p>
        </div>
      </div>
    </div>
  );
}
